import { formatCount, tt } from '@agos/shared'

/**
 * `/notifications` — what the system has noticed, and what it has not been told about.
 *
 * A different page from `/settings-notifications`, which decides how *customers* are told things. This one is
 * the staff inbox: low stock, an invoice past due, a purchase order the system drafted. They share a
 * controller and nothing else, so they stay two pages.
 *
 * **FB-146.** The unread count is computed from the page that happens to be loaded:
 *
 * ```tsx
 * const unreadCount = useMemo(() => list.filter(n => !n.readAt).length, [list]);
 * …
 * description={`${unreadCount} neprečítaných · zobrazené ${list.length} z ${total}`}
 * ```
 *
 * `total` is the server's figure; `unreadCount` is not. On the restored data the inbox holds **164** rows of
 * which **12** are unread, and the first page is 25 — so the header reads correctly only while every unread
 * row happens to be in the loaded window. `GET /notifications/unread-count` exists and answers 12; the page
 * derives a number the server already knows.
 *
 * **FB-147.** `<T>{n.title}</T>` puts **data** through the translation memory. A title is generated —
 * *Nízka zásoba: Múka (2 kg)* — so every notification becomes a dictionary key, and the missing-translation
 * endpoint is called for each one. Titles are already Slovak because the backend wrote them in Slovak; the
 * body beside them is rendered as text, which is the correct treatment for both.
 */

export type Severity = 'info' | 'warning' | 'error'

export interface Notification {
  id: number
  kind: string
  severity: Severity
  title: string
  body: string | null
  refTable: string | null
  refId: number | null
  readAt: string | null
  createdAt: string
}

export interface InboxPage {
  notifications: Notification[]
  /** The server's count of everything matching the filter, not of what was returned. */
  total: number
}

export const PAGE_SIZE = 25

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function pageOf(payload: unknown): InboxPage {
  const row = payload as { notifications?: unknown; total?: unknown } | undefined
  const list = Array.isArray(row?.notifications) ? row.notifications : []
  return {
    notifications: list.map((entry) => {
      const one = entry as Record<string, unknown>
      const severity = one.severity
      return {
        id: typeof one.id === 'number' ? one.id : 0,
        kind: text(one.kind) ?? '',
        severity: severity === 'warning' || severity === 'error' ? severity : 'info',
        title: text(one.title) ?? '',
        body: text(one.body),
        refTable: text(one.refTable),
        refId: typeof one.refId === 'number' ? one.refId : null,
        readAt: text(one.readAt),
        createdAt: text(one.createdAt) ?? '',
      }
    }),
    total: typeof row?.total === 'number' ? row.total : list.length,
  }
}

/** The three kinds the restored data holds, and the label each gets. */
export function kindLabel(kind: string): string {
  if (kind === 'low_stock') return tt('Sklad')
  if (kind === 'invoice_overdue') return tt('Po splatnosti')
  if (kind === 'invoice_due_soon') return tt('Splatnosť čoskoro')
  if (kind === 'reorder_draft') return tt('Návrh objednávky')
  if (kind === 'recurring_invoice') return tt('Opakovaná faktúra')
  // §9.348, the owner's C4/C6. These five arrive from `DailyCheck`, which had no clock until
  // BACKEND.md §10A.136 gave it one - so until this session they were kinds nothing produced, and
  // the fall-through below would have printed `vehicle_document_due` at a person.
  if (kind === 'vehicle_document_expiring') return tt('Vozový park')
  if (kind === 'vehicle_document_due') return tt('Vozový park - do 14 dní')

  if (kind === 'vehicle_document_expired') return tt('Vozový park - prepadnuté')
  if (kind === 'outgoing_invoice_overdue') return tt('Vydaná faktúra po splatnosti')
  if (kind === 'outgoing_invoice_draft_stale') return tt('Nevystavená faktúra')
  // §9.502 - `DoorTransferService` raises this when a driver takes a bank transfer at the door. It had no
  // label, so the fall-through below printed `transfer_paid` at a person - which is exactly what §9.348's
  // own comment says this fall-through does, five kinds earlier.
  // §9.741 - and it is *QR Pay* now, not a transfer: on the bank's page the customer may pay the code by
  // card as well, §10A.390 files either as `qr_pay`, and the notice itself is titled *Platba QR Pay
  // prijatá*. Only the word moved: the server still raises the kind as `transfer_paid`.
  if (kind === 'transfer_paid') return tt('Platba QR Pay')
  /* §9.751, BACKEND.md §10A.405 - the driver said the customer paid one way and the order kept another,
   * because a live receipt (or a payment the bank already took) holds it. The staff are who can storno the
   * receipt and print a new one, so this is their notice. */
  if (kind === 'door_method_kept') return tt('Spôsob platby pri doručení')
  /* §10A.272 - the one kind `POST /api/v1/notifications` raises. The endpoint deliberately does not
   * take a kind from its caller, precisely so this list can stay complete: `kindLabel` falling through
   * to the raw key is §9.502's defect, and an endpoint that can invent a kind makes it unavoidable. */
  if (kind === 'manual') return tt('Oznam')
  /* §10A.270 - the platform answering a ticket, and the platform moving one. Two kinds because
   * *answered* and *your turn* are different sentences: `waiting_on_franchise` exists because the
   * platform has asked a question and cannot proceed. */
  if (kind === 'support_ticket_reply') return tt('Odpoveď na tiket')
  if (kind === 'support_ticket_status') return tt('Stav tiketu')
  /* BACKEND.md §10A.408 - somebody who is not a main administrator changed a role: its rights, through an edge the
   * main administrator drew in *Kto spravuje role*, or a person's role. Raised only for such a change, so the
   * bell tells the main administrator about the changes they did not make themselves. */
  if (kind === 'roles_changed_by_delegate') return tt('Role a práva')
  /* BACKEND.md §10A.438 H, owner O139 - an external courier signed himself up and waits for the restaurant to
   * approve him on *Externý šofér*. */
  if (kind === 'courier_signup') return tt('Externý šofér')
  // A kind nobody has labelled is shown as its key rather than hidden — the key is what a report would
  // be filed against.
  return kind
}

export function isLabelledKind(kind: string): boolean {
  return kindLabel(kind) !== kind
}

/** Where a notification points, when it points anywhere. */
export function linkFor(notification: Notification): string | null {
  /* BACKEND.md §10A.408 - a role a delegate changed points nowhere, on purpose: the bell is every administrator's
   * and *História* is the main administrator's alone, so a link would lead most of its readers to a refusal
   * (§9.7's defect). The server raises it with no table; this holds even if a later raise passes one. */
  if (notification.kind === 'roles_changed_by_delegate') return null
  if (notification.refTable === null) return null
  /* §10A.271 - the guard used to refuse a null `refId` here, for every table. Five of the six branches
   * below ignore the id entirely and answer a bare page, so that turned the one-notice-per-shortage
   * change into a notice that renders correctly and is simply not clickable - §9.502's silent half. Only
   * `invoices` builds a row link, so only `invoices` needs an id. */
  if (notification.refTable === 'stock_items') return '/stock'
  if (notification.refTable === 'invoices') {
    return notification.refId === null ? '/invoices' : `/invoices?openId=${notification.refId}`
  }
  if (notification.refTable === 'reorder_drafts') return '/reorder-drafts'
  // *"integrate it in our system"* - a notice that a van's STK has lapsed pointed nowhere, which is
  // the difference between being told and being able to do something about it.

  if (notification.refTable === 'vehicles') return '/vehicle-fleet'
  if (notification.refTable === 'outgoing_invoices') return '/outgoing-invoices'
  /* §9.502 - the same raise passes `orders`, which had no branch, so a transfer the driver had just been paid
   * pointed nowhere. The `vehicles` note above is the rule: being told and being able to act on it are
   * different things. The bare page rather than a row link, like `stock_items` and `reorder_drafts`, because
   * no manager page reads an id out of the query string. */
  if (notification.refTable === 'orders') return '/orders'
  // §10A.270 - the franchise's own support screen. The bare page, like every branch but `invoices`:
  // the ticket list opens the thread, and no manager page reads an id out of the query string.
  if (notification.refTable === 'support_tickets') return '/support'
  // §10A.438 H - a courier's sign-up is raised against his `employees` row and approved on the external
  // drivers' screen, where the button is.
  if (notification.refTable === 'employees' && notification.kind === 'courier_signup') return '/external-driver'
  return null
}

/**
 * FB-146: the header's numbers, each from where it actually comes from.
 *
 * `unread` is the server's count for the whole inbox. `shown` and `total` describe the window. Legacy mixes
 * a windowed figure with a whole-inbox one in the same sentence.
 */
export interface InboxCounts {
  shown: number
  total: number
  unread: number | null
}

export function countsDescription(counts: InboxCounts): string {
  const window = `${tt('zobrazených')} ${formatCount(counts.shown)} ${tt('z')} ${formatCount(counts.total)}`
  if (counts.unread === null) {
    // The count endpoint failed; a number derived from one page would look like the same fact.
    return `${window} · ${tt('počet neprečítaných sa nepodarilo zistiť')}`
  }
  return `${formatCount(counts.unread)} ${tt('neprečítaných v celej schránke')} · ${window}`
}

/** Whether there is another page to ask for. */
export function hasMore(counts: Pick<InboxCounts, 'shown' | 'total'>): boolean {
  return counts.shown < counts.total
}

export function nextPageSize(counts: Pick<InboxCounts, 'shown' | 'total'>): number {
  return Math.max(0, Math.min(PAGE_SIZE, counts.total - counts.shown))
}

// `severityOrder` was here and is deleted — §9.35, 2026-08-06. A sort key for a list that must not be
// sorted here.
//
// The inbox is **paged** — `hasMore` and `nextPageSize` above are both wired — so the order is the
// server's and the client holds one page of it. Sorting that page by severity would float the errors
// *within the page*, putting page two's errors below page one's notices: a list that looks ordered
// and is not, which is worse than the chronological one it replaced. Nothing called it, and nothing
// should have.

/**
 * What the *daily check* does, said before it is pressed.
 *
 * `DailyCheck.run()` drafts purchase orders and **e-mails every administrator** — its own controller comment
 * says the endpoint is `main_admin` for exactly that reason. Legacy's button fires on the first click.
 */
export function dailyCheckWarning(): string {
  return tt(
    'Kontrola prejde sklad a faktúry, vytvorí návrhy objednávok a pošle e-mail všetkým administrátorom. Beží aj sama, raz denne.',
  )
}

/**
 * What needs doing, from the unread ones - §9.348, the owner's C6.
 *
 * **Unread only, deliberately.** A page of notices somebody has already read needs no attention
 * line; counting all of them would have the owner's four-month-old stock report demanding action for
 * ever, which is the state they described as *"shitty"*.
 *
 * **Errors before warnings, and the kinds named.** *2 things need attention* is a number; *Vozový
 * park, Vydané faktúry* is where to go. The kinds are named in severity order and capped at three -
 * a summary that lists everything is the list it was meant to summarise.
 */
export function inboxSummary(notifications: readonly Notification[]): string {
  const unread = notifications.filter((notification) => notification.readAt === null)
  if (unread.length === 0) return ''

  const errors = unread.filter((notification) => notification.severity === 'error')
  const needing = errors.length > 0 ? errors : unread
  const kinds: string[] = []
  for (const notification of needing) {
    const label = kindLabel(notification.kind)
    if (!kinds.includes(label)) kinds.push(label)
  }
  const named = kinds.slice(0, 3).join(', ')
  const rest = kinds.length > 3 ? ` +${formatCount(kinds.length - 3)}` : ''
  const what = errors.length > 0 ? tt('vyžaduje pozornosť') : tt('neprečítaných')
  return `${formatCount(needing.length)} ${what}: ${named}${rest}`
}

/**
 * What the inbox was missing, and the owner could not name - `FRONTEND.md` §9.423.
 *
 * *"they are missing something important, and I dont know what."* Measured on their own data:
 * **151 notifications, every one of them `warning`/`low_stock`, spanning twelve days, none unread.**
 * The page drew them as 151 near-identical rows, 25 at a time behind a *load more*, with two filters
 * - all, and unread - and nothing unread. So there was no way to tell one row from the next, no way
 * to see the shape of the list, and no way to find the one error in it when the daily check raises
 * one (`CUTOVER.md §7.1` says the first morning raises two).
 *
 * The answer is structure, and it is three things: what kinds are in here and how many, which day
 * each row belongs to, and how long ago that was. None of it is new data - all three are derived
 * from rows the page already had.
 */

/** One severity, how many of it, and whether it is the filter in force. */
export interface SeverityTally {
  severity: Severity | 'all'
  count: number
}

/**
 * How many of each severity are in the loaded rows.
 *
 * <b>Counted over what is loaded, not over the total</b>, and that is a limitation worth stating
 * rather than hiding: the list endpoint takes `unread`, `limit` and `offset` and no severity, so
 * these numbers describe the page in hand. `countsDescription` already says how many of the total
 * are shown, so the two read together honestly - and a tally that claimed to cover 151 rows while
 * seeing 25 would be the kind of figure this repository keeps being caught by
 * (`agos-carry-a-figures-limitation-with-the-figure`).
 */
export function severityTallies(notifications: readonly Notification[]): SeverityTally[] {
  const order: (Severity | 'all')[] = ['all', 'error', 'warning', 'info']
  return order
    .map((severity) => ({
      severity,
      count:
        severity === 'all'
          ? notifications.length
          : notifications.filter((one) => one.severity === severity).length,
    }))
    // A severity with nothing in it is a chip that can only ever disappoint. `all` always shows.
    .filter((tally) => tally.severity === 'all' || tally.count > 0)
}

export function severityLabel(severity: Severity | 'all'): string {
  if (severity === 'error') return tt('Chyby')
  if (severity === 'warning') return tt('Upozornenia')
  if (severity === 'info') return tt('Informácie')
  return tt('Všetky')
}

/** One day's worth of the inbox, newest day first, and newest row first within it. */
export interface InboxDay {
  /** `YYYY-MM-DD`, which is what makes this addressable from a test. */
  key: string
  /** *Dnes*, *Včera*, or the date as a reader would write it. */
  label: string
  notifications: Notification[]
}

/**
 * The rows grouped by the day they arrived - §9.423.
 *
 * <b>Because 151 rows of one kind is not a list, it is a wall.</b> Grouping is the cheapest thing
 * that makes it scannable, and it needs no endpoint: the rows carry `createdAt`.
 *
 * <b>The day is taken from the local date, not from the ISO string's first ten characters.</b> A
 * notification raised at 00:30 Bratislava time is stored as the previous day in UTC, so slicing the
 * string would file it under yesterday and the owner would see two headings for one evening.
 */
export function inboxDays(notifications: readonly Notification[], now = new Date()): InboxDay[] {
  const days = new Map<string, Notification[]>()
  for (const one of notifications) {
    const when = new Date(one.createdAt)
    if (Number.isNaN(when.getTime())) continue
    const key = dayKey(when)
    days.set(key, [...(days.get(key) ?? []), one])
  }
  return [...days.entries()]
    .sort(([left], [right]) => (left < right ? 1 : -1))
    .map(([key, rows]) => ({
      key,
      label: dayLabel(key, now),
      notifications: [...rows].sort((left, right) =>
        left.createdAt < right.createdAt ? 1 : left.createdAt > right.createdAt ? -1 : 0,
      ),
    }))
}

function dayKey(when: Date): string {
  const month = String(when.getMonth() + 1).padStart(2, '0')
  const day = String(when.getDate()).padStart(2, '0')
  return `${when.getFullYear()}-${month}-${day}`
}

function dayLabel(key: string, now: Date): string {
  if (key === dayKey(now)) return tt('Dnes')
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (key === dayKey(yesterday)) return tt('Včera')
  const parts = key.split('-')
  return `${Number(parts[2])}. ${Number(parts[1])}. ${parts[0]}`
}

/**
 * How long ago, in the words somebody says out loud - §9.423.
 *
 * Beside the clock time rather than instead of it: *pred 2 h* is what a reader wants at a glance and
 * the exact minute is what they want when they are working out what happened when. The page had only
 * the second.
 */
export function agoLabel(createdAt: string, now = new Date()): string {
  const when = new Date(createdAt)
  if (Number.isNaN(when.getTime())) return ''
  const seconds = Math.floor((now.getTime() - when.getTime()) / 1000)
  // A clock a little behind the server is normal and *in 3 seconds* is not a thing to print.
  if (seconds < 60) return tt('teraz')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${tt('pred')} ${formatCount(minutes)} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${tt('pred')} ${formatCount(hours)} h`
  const days = Math.floor(hours / 24)
  if (days < 31) return `${tt('pred')} ${formatCount(days)} ${days === 1 ? tt('dňom') : tt('dňami')}`
  return ''
}

/**
 * The subject of a notice, and the category it repeats - `FRONTEND.md` §9.424.
 *
 * **§9.423 grouped by day and that was the wrong axis, which the owner's data shows plainly.** All
 * 151 of their notifications share **one** title prefix - *Sklad pod minimum* - and differ only in
 * the item after the colon. Grouped by day that is twelve headings over twelve near-identical rows
 * each, and every row still repeated the prefix, the kind chip (*Sklad*, 151 times) and a severity
 * chip that read the same number as *all*.
 *
 * So the repeated half becomes the heading and the varying half becomes the row. *Sklad pod
 * minimum - 151*, then `Chipotle dresing`, `Sezam`, `Špagety`. That is the list somebody can read.
 *
 * <b>Split on the first colon, and fall back to the whole title.</b> Every kind `DailyCheck`
 * produces is written `<category>: <subject>`, but a kind that is not - or a title somebody edits
 * later - must still render, so a title with no colon is its own subject and has no category.
 */
export function notificationCategory(title: string): string {
  const colon = title.indexOf(':')
  return colon > 0 ? title.slice(0, colon).trim() : ''
}

export function notificationSubject(title: string): string {
  const colon = title.indexOf(':')
  return colon > 0 && colon < title.length - 1 ? title.slice(colon + 1).trim() : title.trim()
}

/** One category's worth of the inbox, the loudest first, newest row first inside it. */
export interface InboxGroup {
  /** The shared title prefix, or `''` for the notices that have none. */
  key: string
  /** What the heading reads: the shared prefix, or the kind's own label where there is none. */
  label: string
  /** The group's kind, so the heading can carry `data-kind` - it is a group-level fact now. */
  kind: string
  /** The worst severity in the group, so the heading can carry it. */
  severity: Severity
  notifications: Notification[]
}

const RANK: Record<Severity, number> = { error: 0, warning: 1, info: 2 }

/**
 * The rows grouped by what they are about - §9.424.
 *
 * <b>Ordered by severity and then by size</b>, because an inbox is read worst-first: two expired
 * vehicle documents belong above 151 low-stock warnings however many of the latter there are. Inside
 * a group, newest first.
 */
export function inboxGroups(notifications: readonly Notification[]): InboxGroup[] {
  const groups = new Map<string, Notification[]>()
  for (const one of notifications) {
    /* §9.520 - **by `kind`, not by the words before a colon.** This keyed on
       `notificationCategory(one.title)`, which is the text before the first colon of a Slovak sentence - so the
       grouping depended on how a title happened to be punctuated, while the row carried a `kind` column and
       `kindLabel` translated all thirteen.
;
       **§10A.271 turned that into a live defect.** The collapse reworded the stock notice: the owner's 543
       stored rows read *"Sklad pod minimum: Mozzarella"* and every new one reads *"Sklad: 12 poloziek pod
       minimom"*, so one kind would have shown as two groups for the rest of the table's life. */
    const key = one.kind
    groups.set(key, [...(groups.get(key) ?? []), one])
  }
  return [...groups.entries()]
    .map(([key, rows]) => {
      const worst = rows.reduce<Severity>(
        (found, one) => (RANK[one.severity] < RANK[found] ? one.severity : found),
        'info',
      )
      return {
        key,
        /* §9.520 - the kind's own translation, always. `EveryNotificationKindIsLabelledTest` already asserts
           every raised kind has one, so a group heading cannot be a raw snake_case key without the build
           failing first - which is a stronger guarantee than the prefix ever gave. */
        label: kindLabel(key),
        kind: key,
        severity: worst,
        notifications: [...rows].sort((left, right) =>
          left.createdAt < right.createdAt ? 1 : left.createdAt > right.createdAt ? -1 : 0,
        ),
      }
    })
    .sort(
      (left, right) =>
        RANK[left.severity] - RANK[right.severity] ||
        right.notifications.length - left.notifications.length ||
        left.label.localeCompare(right.label),
    )
}

/**
 * Whether the severity chips are worth drawing at all - §9.424.
 *
 * With one severity in the inbox they read *Všetky 151* and *Upozornenia 151* - the same number
 * twice, which is how a filter row becomes clutter that looks like a fault. Two or more severities
 * and they are the fastest way to the errors.
 */
export function severityFilterIsUseful(notifications: readonly Notification[]): boolean {
  return new Set(notifications.map((one) => one.severity)).size > 1
}
